import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../../utils/api';
import { Shield, Plus, Edit3, Trash2, ChevronDown, ChevronRight, X, ToggleLeft, ToggleRight, GripVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';

const COLOR_PRESETS = [
    '#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];

const ClientRolesPanel = ({ clientId }) => {
    const { theme } = useTheme();
    const dark = theme === 'dark';
    const cardBg = dark ? 'bg-gray-800' : 'bg-white';
    const borderColor = dark ? 'border-gray-700' : 'border-gray-200';

    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [expandedRole, setExpandedRole] = useState(null);
    const [featurePerms, setFeaturePerms] = useState(null);
    const [featurePermsLoading, setFeaturePermsLoading] = useState(false);

    const [form, setForm] = useState({ name: '', color: '#6B7280' });
    const [saving, setSaving] = useState(false);

    // Drag state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const cq = clientId ? `?client_id=${clientId}` : '';
    useEffect(() => { fetchRoles(); }, [clientId]);

    const fetchRoles = async () => {
        try {
            const res = await apiGet(`/client_manager/roles${cq}`);
            if (res.ok) {
                const data = await res.json();
                setRoles(data.roles);
            }
        } catch {} finally { setLoading(false); }
    };

    const fetchFeaturePerms = async (roleId) => {
        setFeaturePermsLoading(true);
        try {
            const res = await apiGet(`/client_manager/roles/${roleId}/feature_permissions${cq}`);
            if (res.ok) {
                const data = await res.json();
                setFeaturePerms(data);
            }
        } catch {} finally { setFeaturePermsLoading(false); }
    };

    const handleCreate = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            const res = await apiPost('/client_manager/roles', {
                name: form.name.trim(),
                color: form.color
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                setShowCreateModal(false);
                resetForm();
                fetchRoles();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to create role');
            }
        } catch { toast.error('Failed to create role'); }
        finally { setSaving(false); }
    };

    const handleUpdate = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            const res = await apiPatch(`/client_manager/roles/${editingRole.id}`, {
                name: form.name.trim(),
                color: form.color
            });
            if (res.ok) {
                toast.success('Role updated');
                setEditingRole(null);
                resetForm();
                fetchRoles();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to update');
            }
        } catch { toast.error('Failed to update'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (role) => {
        if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
        try {
            const res = await apiDelete(`/client_manager/roles/${role.id}`);
            if (res.ok) {
                toast.success('Role deleted');
                fetchRoles();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to delete');
            }
        } catch { toast.error('Failed to delete'); }
    };

    const handleToggleFeature = async (roleId, featureId, currentEnabled) => {
        try {
            const res = await apiPatch(`/client_manager/roles/${roleId}/feature_permissions`, {
                features: [{ feature_id: featureId, enabled: !currentEnabled }]
            });
            if (res.ok) {
                setFeaturePerms(prev => ({
                    ...prev,
                    features: prev.features.map(f =>
                        f.feature_id === featureId ? { ...f, enabled: !currentEnabled } : f
                    )
                }));
            }
        } catch { toast.error('Failed to update'); }
    };

    // Drag and drop handlers
    const handleDragStart = (index) => {
        dragItem.current = index;
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        dragOverItem.current = index;
        setDragOverIndex(index);
    };

    const handleDragEnd = async () => {
        setDragOverIndex(null);
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }

        const reordered = [...roles];
        const [moved] = reordered.splice(dragItem.current, 1);
        reordered.splice(dragOverItem.current, 0, moved);
        setRoles(reordered);

        dragItem.current = null;
        dragOverItem.current = null;

        // Send new order to backend (top = highest authority)
        const roleIds = reordered.map(r => r.id);
        try {
            const res = await apiPatch('/client_manager/roles/reorder', { role_ids: roleIds });
            if (!res.ok) {
                toast.error('Failed to save order');
                fetchRoles();
            }
        } catch {
            toast.error('Failed to save order');
            fetchRoles();
        }
    };

    const resetForm = () => setForm({ name: '', color: '#6B7280' });

    const startEdit = (role) => {
        setEditingRole(role);
        setForm({ name: role.name, color: role.color });
    };

    const toggleExpand = (roleId) => {
        if (expandedRole === roleId) {
            setExpandedRole(null);
            setFeaturePerms(null);
        } else {
            setExpandedRole(roleId);
            fetchFeaturePerms(roleId);
        }
    };

    if (loading) return <div className="text-center py-8 text-gray-500 font-rasa">Loading...</div>;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-dm-serif">Roles</h2>
                    <p className="text-sm text-gray-500 font-rasa mt-1">Create and manage roles for your organization. Drag to reorder — top = highest authority.</p>
                </div>
                <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-rasa hover:bg-blue-600">
                    <Plus size={16} /> Create Role
                </button>
            </div>

            {roles.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${cardBg} border ${borderColor}`}>
                    <Shield className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-500 font-rasa">No roles created yet</p>
                    <p className="text-sm text-gray-400 font-rasa mt-1">Create roles to define your organization's structure</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {roles.map((role, index) => (
                        <div
                            key={role.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`rounded-lg border ${borderColor} ${cardBg} overflow-hidden transition-all ${
                                dragOverIndex === index ? (dark ? 'border-blue-500 bg-gray-750' : 'border-blue-500 bg-blue-50') : ''
                            }`}
                        >
                            <div className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3">
                                    <GripVertical size={16} className={`cursor-grab active:cursor-grabbing ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
                                    <button onClick={() => toggleExpand(role.id)} className="p-0.5">
                                        {expandedRole === role.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </button>
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                                    <span className="font-dm-serif text-sm">{role.name}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm font-rasa">
                                    <span className="text-gray-500">{role.user_count} user{role.user_count !== 1 ? 's' : ''}</span>
                                    <button onClick={() => startEdit(role)} className={`p-1 rounded ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><Edit3 size={14} /></button>
                                    <button onClick={() => handleDelete(role)} className="p-1 rounded text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
                                </div>
                            </div>

                            {expandedRole === role.id && (
                                <div className={`border-t ${borderColor} p-4`}>
                                    <h4 className="text-sm font-dm-serif mb-3">Feature Permissions</h4>
                                    {featurePermsLoading ? (
                                        <p className="text-sm text-gray-500 font-rasa">Loading...</p>
                                    ) : featurePerms?.features?.length === 0 ? (
                                        <p className="text-sm text-gray-500 font-rasa">No features enabled for this organization</p>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {featurePerms?.features?.map(f => (
                                                <div key={f.feature_id} className={`flex items-center justify-between p-2 rounded ${dark ? 'bg-gray-750' : 'bg-gray-50'}`}>
                                                    <div>
                                                        <span className="text-sm font-rasa">{f.name}</span>
                                                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-rasa ${dark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>{f.category}</span>
                                                    </div>
                                                    <button onClick={() => handleToggleFeature(role.id, f.feature_id, f.enabled)} className="p-0.5">
                                                        {f.enabled
                                                            ? <ToggleRight size={20} className="text-green-500" />
                                                            : <ToggleLeft size={20} className="text-gray-400" />
                                                        }
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit Modal */}
            {(showCreateModal || editingRole) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowCreateModal(false); setEditingRole(null); resetForm(); }}>
                    <div className={`p-6 rounded-lg w-96 ${cardBg}`} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-dm-serif">{editingRole ? 'Edit Role' : 'Create Role'}</h3>
                            <button onClick={() => { setShowCreateModal(false); setEditingRole(null); resetForm(); }}><X size={18} /></button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-rasa text-gray-500 mb-1">Name *</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'} font-rasa text-sm`}
                                    placeholder="e.g. Director, Team Lead" />
                            </div>
                            <div>
                                <label className="block text-sm font-rasa text-gray-500 mb-1">Color</label>
                                <div className="space-y-3">
                                    <div className="flex gap-2 flex-wrap">
                                        {COLOR_PRESETS.map(c => (
                                            <button key={c} onClick={() => setForm({ ...form, color: c })}
                                                className={`w-8 h-8 rounded-lg transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-blue-500' : 'hover:scale-110'}`}
                                                style={{ backgroundColor: c }} />
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-rasa">Custom color:</label>
                                        <input
                                            type="color"
                                            value={form.color}
                                            onChange={e => setForm({ ...form, color: e.target.value })}
                                            className="w-12 h-8 rounded cursor-pointer"
                                        />
                                        <span className={`text-xs font-rasa ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{form.color.toUpperCase()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => { setShowCreateModal(false); setEditingRole(null); resetForm(); }}
                                className={`px-4 py-2 rounded-lg text-sm font-rasa ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>Cancel</button>
                            <button onClick={editingRole ? handleUpdate : handleCreate} disabled={saving}
                                className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
                                {saving ? 'Saving...' : (editingRole ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientRolesPanel;
